import React from "react";
import { Clock, CalendarClock } from "lucide-react";
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import ActionsDropdown from "./ActionsDropdown";

const Text = ({ children, page = "WAITING_ROOM" }) => (
  <Txt page={page}>{children}</Txt>
);

// map raw statuses to translation keys
const statusKeyMap = {
  waiting: "statusWaiting",
  with_nurse: "statusWithNurse",
  with_doctor: "statusWithDoctor",
  done: "statusDone",
  cancelled: "statusCancelled",
  absent: "statusAbsent",
  payment: "statusPayment",
};

/**
 * Skeleton placeholder rows shown while the waiting room is loading (TableView variant)
 * @returns Skeleton JSX
 */
const TableSkeleton = ({ darkMode }) => {
  const bar = darkMode ? "bg-slate-700" : "bg-slate-200";
  const rowBorder = darkMode ? "border-slate-700" : "border-slate-200";
  return (
    <div className="p-4">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={`flex items-center gap-4 py-4 border-b last:border-b-0 ${rowBorder}`}
        >
          <div className={`w-4 h-4 rounded shrink-0 ${bar} animate-pulse`} />
          <div className={`h-4 flex-1 max-w-[200px] rounded ${bar} animate-pulse`} />
          <div className={`h-4 w-16 rounded ${bar} animate-pulse`} />
          <div className={`h-4 w-20 rounded ${bar} animate-pulse`} />
          <div className={`h-4 w-20 rounded ${bar} animate-pulse`} />
          <div className={`w-8 h-8 rounded-md ${bar} animate-pulse`} />
        </div>
      ))}
    </div>
  );
};
/**
 * Table view of the waiting room.
 * Props:
 * - loading: whether data is still being fetched
 * - empty: React node rendered when there are no entries
 * - items: array of pre-computed patient item descriptors
 * - viewProps: shared handlers/state object (see WaitingRoom.jsx)
 * - darkMode: boolean
 * @returns Table JSX
 */
const TableView = ({ loading, empty, items, viewProps, darkMode }) => {
  const headerBg = darkMode ? "bg-slate-900/40" : "bg-slate-50";
  const headerText = darkMode ? "text-slate-300" : "text-slate-600";
  const border = darkMode ? "border-slate-700" : "border-slate-200";
  const rowHover = darkMode ? "hover:bg-slate-800/60" : "hover:bg-slate-50";
  const cellText = darkMode ? "text-slate-200" : "text-slate-800";
  const mutedText = darkMode ? "text-slate-400" : "text-slate-500";

  return (
    <div
      className={arClass(
        `rounded-2xl border shadow-sm overflow-hidden ${
          darkMode ? "bg-slate-800" : "bg-white"
        }`,
      )}
    >
      {loading ? (
        <div className={arClass(`border-b ${border}`)}>
          <TableSkeleton darkMode={darkMode} />
        </div>
      ) : items.length === 0 ? (
        empty
      ) : (
        <div className={arClass("overflow-x-auto")}>
          <table className={arClass("w-full text-sm min-w-[760px]")}>
            <thead>
              <tr className={arClass(`border-b ${headerBg}`)}>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap w-10 ${headerText}`,
                  )}
                >
                  <span className="sr-only">Select</span>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <Text page="PATIENTS">Patient</Text>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <Text page="PATIENTS">Phone</Text>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <Text>wait</Text>
                  </span>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <Text>urgency</Text>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <Text>status</Text>
                </th>
                <th
                  className={arClass(
                    `py-3 px-4 text-xs font-semibold text-left whitespace-nowrap ${headerText}`,
                  )}
                >
                  <Text>actions</Text>
                </th>
              </tr>
            </thead>
            <tbody>
{items.map((it) => (
                <tr
                  key={it.entry.id}
                  className={arClass(
                    `border-b last:border-b-0 transition-colors ${border} ${rowHover}`,
                  )}
                >
                  <td className={arClass("py-3 px-4 w-10")}>
                    <input
                      type="checkbox"
                      checked={viewProps.selectedIds.includes(it.entry.id)}
                      onChange={() => viewProps.handleSelectItem(it.entry.id)}
                      disabled={viewProps.canDeleteEntries ? false : true}
                      className="w-4 h-4 cursor-pointer rounded-md"
                    />
                  </td>
                  <td className={arClass(`py-3 px-4 ${cellText}`)}>
                    <div className="font-semibold whitespace-nowrap">
                      {it.patientName}
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${mutedText}`}>
                      {it.entry.position !== null && (
                        <span>
                          <Text>position</Text> #{it.entry.position}
                        </span>
                      )}
                      {it.entry.visitId && (
                        <span className="flex items-center gap-1">
                          <CalendarClock className="w-3.5 h-3.5" />
                          <Text page="VISITS">Visit</Text> #{it.entry.visitId}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={arClass(`py-3 px-4 whitespace-nowrap ${mutedText}`)}>
                    {it.entry.patient?.phone || "—"}
                  </td>
                  <td className={arClass(`py-3 px-4 whitespace-nowrap ${cellText}`)}>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {it.minutes}
                    </span>
                  </td>
                  <td className={arClass("py-3 px-4 whitespace-nowrap")}>
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide inline-flex justify-center items-center ${it.urgencyBadge}`}
                    >
                      <Text>{it.urgency}</Text>
                    </span>
                  </td>
                  <td className={arClass("py-3 px-4 whitespace-nowrap")}>
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide inline-flex justify-center items-center ${it.statusBadge}`}
                    >
                      <Text>{statusKeyMap[it.entry.status]}</Text>
                    </span>
                  </td>
                  <td className={arClass("py-3 px-4 whitespace-nowrap")}>
                    <ActionsDropdown
                      entry={it.entry}
                      actionsOpenId={viewProps.actionsOpenId}
                      anchorEl={viewProps.anchorEl}
                      onCloseActions={viewProps.onCloseActions}
                      defaultLanguage={viewProps.defaultLanguage}
                      darkMode={viewProps.darkMode}
                      theme={viewProps.theme}
                      actionLoading={viewProps.actionLoading}
                      showActionDescription={viewProps.showActionDescription}
                      setShowActionDescription={viewProps.setShowActionDescription}
                      roles={viewProps.roles}
                      isAdmin={viewProps.isAdmin}
                      handleStatusChange={viewProps.handleStatusChange}
                      handleDeleteEntry={viewProps.handleDeleteEntry}
                      toggleActions={viewProps.toggleActions}
                    />
                  </td>
                </tr>
              ))}
</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TableView;